import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/Card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/Table";
import { formatNumber, formatDecimal, formatSignedNumber, deltaTextClass } from "../FinancialReview/format";

function DeltaTable({ title, rows, emptyLabel, startLabel, endLabel }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Super org</TableHead>
                <TableHead>{endLabel}</TableHead>
                <TableHead>{startLabel}</TableHead>
                <TableHead>Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.super_org}>
                  <TableCell>{row.super_org}</TableCell>
                  <TableCell>{formatNumber(row.current_licensed)}</TableCell>
                  <TableCell>{formatNumber(row.previous_licensed)}</TableCell>
                  <TableCell className={deltaTextClass(row.delta)}>{formatSignedNumber(row.delta)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default function AnalysisTab({ data, loading, error }) {
  if (loading && !data) return <p className="text-sm text-muted-foreground">Loading Analysis report…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return null;

  const { series = [], top_growth = [], top_declines = [], start_month, end_month } = data;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Analysis</h2>
        <p className="text-sm text-muted-foreground">
          {start_month} through {end_month}
          {series.length ? ` · ${series.length} month${series.length === 1 ? "" : "s"}` : ""}
        </p>
      </div>

      {series.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Monthly trend</CardTitle>
            <CardDescription>Licensed users each month in the range</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="licensed" name="Licensed" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">By month</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead>Licensed</TableHead>
                  <TableHead>Recordings</TableHead>
                  <TableHead>Avg DAU</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {series.map((row, index) => {
                  const prev = index > 0 ? series[index - 1] : null;
                  return (
                    <TableRow key={row.month}>
                      <TableCell className="whitespace-nowrap">{row.month}</TableCell>
                      <TableCell>
                        {formatNumber(row.licensed)}
                        {prev && (
                          <span className={`ml-1 text-xs ${deltaTextClass(row.licensed - prev.licensed)}`}>
                            {formatSignedNumber(row.licensed - prev.licensed)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatNumber(row.recordings)}
                        {prev && (
                          <span className={`ml-1 text-xs ${deltaTextClass(row.recordings - prev.recordings)}`}>
                            {formatSignedNumber(row.recordings - prev.recordings)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{formatDecimal(row.avg_dau)}</TableCell>
                    </TableRow>
                  );
                })}
                {series.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      No months in the selected range.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DeltaTable
          title="Top growth (licensed)"
          rows={top_growth}
          emptyLabel="No super orgs gained licensed users across this range."
          startLabel={start_month}
          endLabel={end_month}
        />
        <DeltaTable
          title="Top declines (licensed)"
          rows={top_declines}
          emptyLabel="No super orgs lost licensed users across this range."
          startLabel={start_month}
          endLabel={end_month}
        />
      </div>
    </div>
  );
}
